import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import {
  IonContent, IonHeader, IonPage, IonTitle,
  IonToolbar, IonButtons, IonMenuButton,
} from '@ionic/react';

const DHARTI_URL = process.env.REACT_APP_DHARTI_URL || 'http://localhost:8000';
const TOKEN_KEY = 'access_token';

const DhartiPage: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeReady = useRef(false);
  const location = useLocation();
  // Keep latest initialContext in a ref so the message handler always sees it
  const initialContextRef = useRef<string | null>(null);

  const iframeSrc = `${DHARTI_URL}/chat-ui?embedded=1&parent=${encodeURIComponent(window.location.origin)}`;

  const sendToIframe = (msg: { type: string; token?: string; mode?: string; text?: string }) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(msg, DHARTI_URL);
  };

  // Sync initialContext from location state; if iframe is already ready, prefill immediately
  useEffect(() => {
    const ctx = (location.state as any)?.initialContext || null;
    initialContextRef.current = ctx;
    if (ctx && iframeReady.current) {
      sendToIframe({ type: 'prefill', text: ctx });
    }
  }, [(location.state as any)?.initialContext]);

  // Auth handshake
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== DHARTI_URL) return;
      const data = event.data || {};
      if (data.type === 'ready') {
        iframeReady.current = true;
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) sendToIframe({ type: 'auth', token });
        const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        sendToIframe({ type: 'theme', mode: isDark ? 'dark' : 'light' });
        if (initialContextRef.current) {
          sendToIframe({ type: 'prefill', text: initialContextRef.current });
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Watch token changes
  useEffect(() => {
    let lastToken = localStorage.getItem(TOKEN_KEY);
    const check = () => {
      const current = localStorage.getItem(TOKEN_KEY);
      if (current !== lastToken) {
        lastToken = current;
        if (iframeReady.current) {
          if (current) sendToIframe({ type: 'auth', token: current });
          else sendToIframe({ type: 'logout' });
        }
      }
    };
    const interval = window.setInterval(check, 2000);
    window.addEventListener('storage', check);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', check);
    };
  }, []);

  // Watch system theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      sendToIframe({ type: 'theme', mode: e.matches ? 'dark' : 'light' });
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Dharti</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent scrollY={false}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            allow="microphone; clipboard-write"
            sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-modals allow-popups allow-popups-to-escape-sandbox"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            title="Dharti"
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default DhartiPage;
